import React, { useState } from 'react';
import Cropper from 'react-easy-crop';
import { Row, Col, Button, Form } from 'react-bootstrap';
import ImgDialog from './ImgDialog';
import { getOrientation } from 'get-orientation';
import { compressFileSize, getCroppedImg, getRotatedImage, imageBucketUrl } from 'utils';
import useTranslate from 'hooks/useTranslate';
import { useBucketUrlMutation, useUploadFileMutation } from 'services';
import toast from 'react-hot-toast';
import Loader from 'components/Common/Loader';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles.scss';

const ORIENTATION_TO_ANGLE: any = {
	'3': 180,
	'6': 90,
	'8': -90,
};

const ImageCropper: React.FC<any> = ({ updateImageData, imgRation = 1 / 1 }) => {
	const [imageSrc, setImageSrc] = useState<any | null>(null);
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [rotation, setRotation] = useState(0);
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
	const [croppedImage, setCroppedImage] = useState<string | null>(null);
	const [isUploading, setIsUploading] = useState(false);

	const { mutateAsync: bucketUrlMutation } = useBucketUrlMutation();
	const { mutateAsync: uploadFileMutation } = useUploadFileMutation();

	const { translate } = useTranslate();

	const onCropComplete = (_croppedArea: any, croppedAreaPixels: any) => {
		setCroppedAreaPixels(croppedAreaPixels);
	};

	const showCroppedImage = async () => {
		try {
			setIsUploading(true);
			const croppedImage: any = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
			const response = await fetch(croppedImage);
			const blobData: any = await response.blob();
			const file: any = await compressFileSize(blobData);
			const imgPath = await imageBucketUrl(
				file,
				translate,
				'image/png',
				file.size,
				bucketUrlMutation,
				uploadFileMutation,
			);

			if (imgPath) {
				updateImageData(imgPath);
				setIsUploading(false);
				setCroppedImage(imgPath);
			} else {
				toast.error(translate('Validation.pleaseTryAgain'));
			}
			setIsUploading(false);
		} catch (e) {
			console.error(e);
		}
	};

	const onModalClose = () => {
		setCroppedImage(null);
	};

	const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			const file = e.target.files[0];
			const imageDataUrl = await readFile(file);

			try {
				const orientation = await getOrientation(file);
				const rotation = ORIENTATION_TO_ANGLE[orientation];
				if (rotation) {
					const rotatedImage = await getRotatedImage(imageDataUrl, rotation);
					setImageSrc(rotatedImage);
				} else {
					setImageSrc(imageDataUrl);
				}
			} catch (e) {
				console.warn('failed to detect the orientation');
			}
		}
	};

	function readFile(file: File): Promise<string> {
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.addEventListener('load', () => resolve(reader.result as string), false);
			reader.readAsDataURL(file);
		});
	}

	return (
		<>
			{imageSrc ? (
				<React.Fragment>
					<Row>
						<Col>
							<div style={{ position: 'relative', width: '100%', height: 400 }}>
								<Cropper
									image={imageSrc}
									crop={crop}
									rotation={rotation}
									zoom={zoom}
									aspect={imgRation}
									onCropChange={setCrop}
									onRotationChange={setRotation}
									onCropComplete={onCropComplete}
									onZoomChange={setZoom}
									zoomSpeed={0.4}
								/>
							</div>
						</Col>
					</Row>
					<Row>
						<Col>
							<div className="zoom-range my-5">
								<Form.Label>Zoom</Form.Label>
								<Form.Control
									type="range"
									min={1}
									max={3}
									step={0.1}
									value={zoom}
									onChange={(e) => setZoom(Number(e.target.value))}
								/>
							</div>
						</Col>
					</Row>
					<Row>
						<div className="edit-modal-actions d-flex gap-4">
							{isUploading ? (
								<Loader />
							) : (
								<Button className="filled-btn rounded-pill" onClick={showCroppedImage}>
									{translate('Common.save')}
								</Button>
							)}
						</div>
					</Row>
					<ImgDialog img={croppedImage} onClose={onModalClose} />
				</React.Fragment>
			) : (
				<Form.Control type="file" onChange={onFileChange} accept="image/*" />
			)}
		</>
	);
};

export default ImageCropper;
